/**
 * BenchmarkTable — Operational Performance Comparison Table
 * Phase 3 — shows efficiency, queue time, lead time per dimension (user/role/process)
 * with top 10% green / bottom 10% red highlights and ranking.
 */

import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getBenchmarkByUser,
  getBenchmarkByRole,
  getBenchmarkByProcess,
  type BenchmarkDimension,
  type BenchmarkRow,
  type BenchmarkResult,
} from '../services/benchmarkService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtPct(v: number | null): string {
  if (v === null) return '—';
  return `${Math.round(v * 100)}%`;
}

function fmtMin(v: number | null): string {
  if (v === null) return '—';
  if (v >= 60) return `${(v / 60).toFixed(1)}h`;
  return `${Math.round(v)}m`;
}

function fmtRank(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `${rank}º`;
}

const TIER_STYLES: Record<BenchmarkRow['tier'], { row: string; badge: string; label: string }> = {
  top: {
    row: 'bg-emerald-500/5 hover:bg-emerald-500/10',
    badge: 'bg-emerald-500/20 text-emerald-400',
    label: 'Top',
  },
  bottom: {
    row: 'bg-red-500/5 hover:bg-red-500/10',
    badge: 'bg-red-500/20 text-[var(--risk-critical)]',
    label: 'Baixo',
  },
  middle: {
    row: 'hover:bg-[var(--button-hover)]',
    badge: 'bg-[var(--bg-surface)] text-[var(--text-secondary)]',
    label: '',
  },
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BenchmarkTableProps {
  organizationId: string;
  /** ISO date strings */
  startDate: string;
  endDate: string;
  /** Which dimension to display — can be controlled externally or internal */
  defaultDimension?: BenchmarkDimension;
  /** Minimum sample size to include a row */
  minSampleSize?: number;
  className?: string;
}

// ---------------------------------------------------------------------------
// Sort state
// ---------------------------------------------------------------------------

type SortKey = 'rank' | 'efficiency' | 'avgQueueMinutes' | 'avgLeadMinutes' | 'sampleSize';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BenchmarkTable: React.FC<BenchmarkTableProps> = ({
  organizationId,
  startDate,
  endDate,
  defaultDimension = 'role',
  minSampleSize = 1,
  className,
}) => {
  const [dimension, setDimension] = useState<BenchmarkDimension>(defaultDimension);
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [sortAsc, setSortAsc] = useState(true);

  const queryFn = useCallback(async (): Promise<BenchmarkResult> => {
    const filters = { organizationId, startDate, endDate, minSampleSize };
    if (dimension === 'user') return getBenchmarkByUser(filters);
    if (dimension === 'process') return getBenchmarkByProcess(filters);
    return getBenchmarkByRole(filters);
  }, [dimension, organizationId, startDate, endDate, minSampleSize]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['benchmark', dimension, organizationId, startDate, endDate, minSampleSize],
    queryFn,
    staleTime: 2 * 60 * 1000,
  });

  const handleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortAsc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortAsc(key === 'rank');
    }
  }, [sortKey]);

  const sortedRows = React.useMemo(() => {
    if (!data?.rows) return [];
    return [...data.rows].sort((a, b) => {
      const av = a[sortKey] ?? Infinity;
      const bv = b[sortKey] ?? Infinity;
      return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [data?.rows, sortKey, sortAsc]);

  const DIMENSION_LABELS: Record<BenchmarkDimension, string> = {
    user: 'Operador',
    role: 'Cargo',
    process: 'Processo',
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronDown size={10} className="opacity-20" />;
    return sortAsc ? <ChevronUp size={10} className="opacity-70" /> : <ChevronDown size={10} className="opacity-70" />;
  };

  const thClass = 'px-3 py-1.5 text-left text-[9px] font-semibold uppercase tracking-[0.08em] text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)] transition-colors';

  return (
    <div className={`flex flex-col gap-2 ${className ?? ''}`}>
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--text-primary)] leading-none">
            Benchmark Operacional
          </span>
          {data && (
            <span className="mt-0.5 text-[10px] text-[var(--text-secondary)]">
              {sortedRows.length === 0
                ? 'Sem execuções concluídas no período'
                : <>{sortedRows.length} entidades · efic. média{' '}
                    <strong className="text-[var(--text-primary)]">{fmtPct(data.avg)}</strong>
                  </>}
            </span>
          )}
        </div>
        {/* Dimension toggle */}
        <div className="inline-flex items-center overflow-hidden rounded border border-[var(--border-default)] bg-[var(--bg-surface)]">
          {(['role', 'user', 'process'] as BenchmarkDimension[]).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDimension(d)}
              className={`px-2.5 py-[3px] text-[9px] font-semibold uppercase tracking-wide transition-colors ${
                dimension === d
                  ? 'bg-[var(--nav-active-bar)] text-white'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--button-hover)]'
              }`}
            >
              {DIMENSION_LABELS[d]}
            </button>
          ))}
        </div>
      </div>

      {/* Summary chips */}
      {data && (
        <div className="flex items-center gap-2">
          {data.best && (
            <div className="flex items-center gap-1 rounded bg-emerald-500/10 border border-emerald-500/20 px-2 py-1">
              <TrendingUp size={10} className="text-emerald-400 shrink-0" />
              <span className="text-[9px] text-emerald-400 font-semibold leading-none">
                Melhor: <strong>{data.best.label}</strong> ({fmtPct(data.best.efficiency)})
              </span>
            </div>
          )}
          {data.worst && (
            <div className="flex items-center gap-1 rounded bg-red-500/10 border border-red-500/20 px-2 py-1">
              <TrendingDown size={10} className="text-[var(--risk-critical)] shrink-0" />
              <span className="text-[9px] text-[var(--risk-critical)] font-semibold leading-none">
                Pior: <strong>{data.worst.label}</strong> ({fmtPct(data.worst.efficiency)})
              </span>
            </div>
          )}
          {data.avg !== null && (
            <div className="flex items-center gap-1 rounded bg-[var(--bg-surface)] border border-[var(--border-default)] px-2 py-1">
              <Minus size={10} className="text-[var(--text-secondary)] shrink-0" />
              <span className="text-[9px] text-[var(--text-secondary)] font-semibold leading-none">
                Média: <strong className="text-[var(--text-primary)]">{fmtPct(data.avg)}</strong>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-auto rounded border border-[var(--border-default)] bg-[var(--bg-elevated)]">
        <table className="w-full min-w-[560px] border-collapse text-[10px]">
          <thead>
            <tr className="border-b border-[var(--border-default)] bg-[var(--bg-surface)]">
              <th className={`${thClass} w-8 text-center`} onClick={() => handleSort('rank')}>
                <span className="inline-flex items-center gap-0.5"># <SortIcon col="rank" /></span>
              </th>
              <th className={thClass}>
                {DIMENSION_LABELS[dimension]}
              </th>
              <th className={thClass} onClick={() => handleSort('efficiency')}>
                <span className="inline-flex items-center gap-0.5">Qualidade <SortIcon col="efficiency" /></span>
              </th>
              <th className={thClass}>SLA</th>
              <th className={thClass} onClick={() => handleSort('avgQueueMinutes')}>
                <span className="inline-flex items-center gap-0.5">Fila média <SortIcon col="avgQueueMinutes" /></span>
              </th>
              <th className={thClass} onClick={() => handleSort('avgLeadMinutes')}>
                <span className="inline-flex items-center gap-0.5">Lead time <SortIcon col="avgLeadMinutes" /></span>
              </th>
              <th className={`${thClass} text-right`} onClick={() => handleSort('sampleSize')}>
                <span className="inline-flex items-center justify-end gap-0.5">n <SortIcon col="sampleSize" /></span>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-[var(--text-secondary)]">
                  <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[var(--border-default)] border-t-[var(--brand-primary)]" />
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-[var(--risk-critical)] text-[10px]">
                  Erro ao carregar dados
                </td>
              </tr>
            )}
            {!isLoading && !isError && sortedRows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-[10px] text-[var(--text-muted)]">
                  Sem execuções concluídas no período selecionado
                </td>
              </tr>
            )}
            {sortedRows.map((row) => {
              const style = TIER_STYLES[row.tier];
              const effPct = row.efficiency !== null ? row.efficiency * 100 : null;
              return (
                <tr
                  key={row.id}
                  className={`border-b border-[var(--border-default)]/50 transition-colors ${style.row}`}
                >
                  {/* Rank */}
                  <td className="px-3 py-1.5 text-center font-mono text-[10px] text-[var(--text-secondary)]">
                    {fmtRank(row.rank)}
                  </td>
                  {/* Label */}
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-[var(--text-primary)] leading-none">{row.label}</span>
                      {style.label && (
                        <span className={`rounded px-1 py-px text-[8px] font-bold uppercase leading-none ${style.badge}`}>
                          {style.label}
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Efficiency / Quality */}
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-[var(--bg-app)]">
                        <div
                          className={`h-full rounded-full ${
                            effPct === null ? 'w-0'
                            : effPct >= 90 ? 'bg-emerald-400'
                            : effPct >= 70 ? 'bg-amber-400'
                            : 'bg-red-400'
                          }`}
                          style={{ width: `${Math.min(effPct ?? 0, 100)}%` }}
                        />
                      </div>
                      <span className={`font-mono font-semibold tabular-nums ${
                        effPct === null ? 'text-[var(--text-muted)]'
                        : effPct >= 90 ? 'text-emerald-400'
                        : effPct >= 70 ? 'text-amber-400'
                        : 'text-[var(--risk-critical)]'
                      }`}>
                        {fmtPct(row.efficiency)}
                      </span>
                    </div>
                  </td>
                  {/* SLA on-time rate */}
                  <td className="px-3 py-1.5">
                    {row.slaOnTimeRate !== null ? (
                      <span className={`font-mono text-[10px] font-semibold tabular-nums ${
                        row.slaOnTimeRate >= 0.95 ? 'text-emerald-400'
                        : row.slaOnTimeRate >= 0.8 ? 'text-amber-400'
                        : 'text-[var(--risk-critical)]'
                      }`}>
                        {Math.round(row.slaOnTimeRate * 100)}%
                      </span>
                    ) : (
                      <span className="text-[var(--text-muted)]">—</span>
                    )}
                  </td>
                  {/* Avg queue */}
                  <td className="px-3 py-1.5 font-mono text-[var(--text-secondary)] tabular-nums">
                    {fmtMin(row.avgQueueMinutes)}
                  </td>
                  {/* Avg lead */}
                  <td className="px-3 py-1.5 font-mono text-[var(--text-secondary)] tabular-nums">
                    {fmtMin(row.avgLeadMinutes)}
                  </td>
                  {/* Sample size */}
                  <td className="px-3 py-1.5 text-right font-mono text-[var(--text-muted)] tabular-nums">
                    {row.sampleSize}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BenchmarkTable;
